import React, { useEffect, useState } from 'react';

const useEnhancedEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

type INoSsrProps = {
    /**
     * If `true`, the component will not only prevent server-side rendering.
     * It will also defer the rendering of the children into a different screen frame.
     */
    defer?: boolean;
    /**
     * The fallback content to display.
     */
    fallback?: JSX.Element;
};

/**
 * NoSsr purposely removes components from the subject of Server Side Rendering (SSR).
 *
 * This component can be useful in a variety of situations:
 * - Escape hatch for broken dependencies not supporting SSR.
 * - Improve the time-to-first paint on the client by only rendering above the fold.
 * - Reduce the rendering time on the server.
 * - Under too heavy server load, you can turn on service degradation.
 *
 * https://material-ui.com/ru/components/no-ssr/
 *
 * https://github.com/mui-org/material-ui/blob/49949e69284cd90eb0ec78f0b8dfe7af3bc138a8/packages/material-ui/src/NoSsr/NoSsr.js
 *
 * Другая реализация: https://github.com/kadirahq/react-no-ssr
 */
const NoSsr: React.FC<INoSsrProps> = ({ children, defer = false, fallback = null }) => {
    const [mountedState, setMountedState] = useState(false);

    useEnhancedEffect(() => {
        if (!defer) {
            setMountedState(true);
        }
    }, [defer]);

    useEffect(() => {
        if (defer) {
            setMountedState(true);
        }
    }, [defer]);

    // We need the Fragment here to force react-docgen to recognise NoSsr as a component.
    return <React.Fragment>{mountedState ? children : fallback}</React.Fragment>;
};

export default NoSsr;
